import { X } from 'lucide-react';
import { fmtRange } from '@/lib/format';

/**
 * THE EVENT'S OWN BAND, ACROSS THE TOP OF A SHEET.
 *
 * The art blown up and blurred behind, the poster itself in front, the title
 * and the date beside it. Anything the page opens over itself should feel
 * like the page, not like being handed off to a form on a different site.
 *
 * Shared rather than copied: the registration sheet has worn this since it
 * was built, and the questions sheet used to open with a plain kicker, so
 * asking the event's host a question felt like a different product from
 * registering for the same night.
 *
 * `children` is whatever that sheet hangs under the band — the registration
 * sheet's step rail. A sheet with no steps passes nothing and the band simply
 * ends.
 *
 * The close button lives in here because the band is dark and the sheet's own
 * button is drawn for a light panel. Callers pass `hideCloseButton`.
 */
export function EventSheetBand({
  image,
  title,
  start,
  end,
  onClose,
  children,
}: {
  image: string | null | undefined;
  title: string;
  start: string;
  end: string | null | undefined;
  onClose: () => void;
  children?: React.ReactNode;
}) {
  return (
    <div className="ed bg-navy relative overflow-hidden">
      {image && (
        <>
          <img
            src={image}
            alt=""
            className="pointer-events-none absolute inset-0 h-full w-full scale-110 object-cover blur-2xl"
          />
          <div className="absolute inset-0 bg-black/45" />
        </>
      )}
      <div className="relative flex items-center gap-3 px-5 pt-5 pb-3">
        {image && (
          <img
            src={image}
            alt=""
            className="aspect-video w-20 shrink-0 rounded-xl object-cover shadow-lg ring-1 ring-white/20"
          />
        )}
        <div className="min-w-0 flex-1">
          <div className="truncate text-base font-extrabold text-white">{title}</div>
          <div className="truncate text-xs text-white/70">{fmtRange(start, end)}</div>
        </div>
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur transition hover:bg-white/25"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      {children}
    </div>
  );
}
